import React, { useState, useEffect } from "react";
import * as api from "../api";
import { C, R, F, numeric } from "../theme";
import { Icon } from "../icons";
import { Btn, Badge, Card, Spinner, Callout, EmptyState, Ref } from "./UI";

function DeliveryChoice({ orderId, setView }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const DAYS_FR_LONG = ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"];

  useEffect(() => {
    (async () => {
      try {
        const o = await api.getOrder(orderId);
        setOrder(o);
        if (o.delivery_date && o.delivery_time) setSuccess(true);
      } catch { setError("Commande introuvable"); }
      finally { setLoading(false); }
    })();
  }, [orderId]);

  useEffect(() => {
    if (!selectedDate) { setAvailableSlots([]); return; }
    (async () => {
      setSlotsLoading(true);
      try {
        const data = await api.getAvailableDeliverySlots(selectedDate);
        const slots = (data.slots || []).filter(s => s.available !== false);
        setAvailableSlots(slots);
        setSelectedSlot("");
      } catch { setAvailableSlots([]); }
      finally { setSlotsLoading(false); }
    })();
  }, [selectedDate]);

  async function handleSubmit() {
    if (!selectedDate || !selectedSlot) return;
    setSubmitting(true);
    try {
      await api.chooseDeliverySlot(orderId, selectedDate, selectedSlot);
      setSuccess(true);
      const o = await api.getOrder(orderId);
      setOrder(o);
    } catch (err) { setError(err.message || "Erreur lors de la confirmation"); }
    finally { setSubmitting(false); }
  }

  // Generate next 14 days for date selection
  const futureDates = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    futureDates.push(d);
  }

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg }}>
      <Spinner />
    </div>
  );

  if (error && !order) return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <Card style={{ maxWidth: 420, width: "100%", padding: 8 }}>
        <EmptyState
          icon="error"
          title={error}
          description="Vérifiez le lien reçu ou contactez-nous pour retrouver votre commande."
          action={<Btn onClick={() => setView("landing")}>Retour à l'accueil</Btn>}
        />
      </Card>
    </div>
  );

  if (success) return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      <div style={{ background: C.navy, color: C.inkOn, padding: "22px 20px 30px" }}>
        <div style={{ maxWidth: 480, margin: "0 auto", display: "flex", alignItems: "center", gap: 11 }}>
          <span style={{
            width: 34, height: 34, borderRadius: R.md, flexShrink: 0,
            background: "rgba(255,255,255,.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="success" size={19} color={C.inkOn} />
          </span>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: F.size["2xl"], fontWeight: F.weight.semi, letterSpacing: "-.02em" }}>
              Livraison confirmée
            </h1>
            <p style={{ fontSize: F.size.base, opacity: .8, marginTop: 2 }}>
              Votre créneau de livraison a été enregistré
            </p>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 480, margin: "0 auto", padding: 20 }}>
        <Card className="animate-fadeInUp" style={{ marginTop: -16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: F.size.sm, color: C.inkMuted }}>Commande</div>
              <Ref style={{ fontSize: F.size.lg }}>{order.id}</Ref>
              <div style={{ fontSize: F.size.md, color: C.inkBody, marginTop: 2 }}>{order.client}</div>
            </div>
            <Badge status={order.status} />
          </div>

          <div style={{
            marginTop: 14, padding: "12px 14px",
            background: C.successSoft, border: `1px solid ${C.successBorder}`, borderRadius: R.md,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: F.size.sm, color: C.inkMuted }}>
              <Icon name="calendar" size={13} />
              Date de livraison
            </div>
            <div style={{
              fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.success, marginTop: 4, ...numeric,
            }}>
              {order.delivery_date} — {order.delivery_time}
            </div>
          </div>

          <div style={{
            display: "flex", alignItems: "flex-start", gap: 6,
            fontSize: F.size.base, color: C.inkMuted, marginTop: 12,
          }}>
            <Icon name="location" size={13} style={{ marginTop: 2 }} />
            <span>Livraison à l'adresse : <strong style={{ color: C.inkBody }}>{order.address}</strong></span>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            <Btn variant="white" icon="home" onClick={() => { setView("landing"); window.history.replaceState({}, "", window.location.pathname); }}>
              Retour à l'accueil
            </Btn>
            <Btn icon="track" onClick={() => {
              window.history.replaceState({}, "", `?track=${order.id}`);
              setView("track");
            }}>
              Suivre ma commande
            </Btn>
          </div>
        </Card>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      <div style={{ background: C.navy, color: C.inkOn, padding: "22px 20px 30px" }}>
        <div style={{ maxWidth: 520, margin: "0 auto", display: "flex", alignItems: "center", gap: 11 }}>
          <span style={{
            width: 34, height: 34, borderRadius: R.md, flexShrink: 0,
            background: "rgba(255,255,255,.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="delivery" size={19} color={C.inkOn} />
          </span>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: F.size["2xl"], fontWeight: F.weight.semi, letterSpacing: "-.02em" }}>
              Choisissez votre créneau
            </h1>
            <p style={{ fontSize: F.size.base, opacity: .8, marginTop: 2 }}>
              Vos vêtements sont prêts. Sélectionnez la date et l'heure de livraison.
            </p>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 520, margin: "0 auto", padding: 20 }}>
        {/* Order info */}
        <Card style={{ marginTop: -16, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: F.size.sm, color: C.inkMuted }}>Commande</div>
              <Ref style={{ fontSize: F.size.lg }}>{order.id}</Ref>
              <div style={{ fontSize: F.size.md, color: C.inkBody, marginTop: 2 }}>{order.client}</div>
            </div>
            <Badge status={order.status} />
          </div>
          {order.price && (
            <div style={{
              marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <span style={{ fontSize: F.size.base, color: C.inkMuted }}>Montant</span>
              <span style={{ fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.ink, ...numeric }}>
                {Number(order.price).toLocaleString()} FCFA
              </span>
            </div>
          )}
        </Card>

        {error && <Callout tone="danger" style={{ marginBottom: 12 }}>{error}</Callout>}

        {order.status !== "Prêt" ? (
          <Card padded={false}>
            <EmptyState
              icon={order.status === "Livré" || order.status === "Récupéré" ? "success" : "pending"}
              title={
                order.status === "Livré"
                  ? "Cette commande a déjà été livrée."
                  : order.status === "Récupéré"
                  ? "Cette commande a été récupérée."
                  : "Votre commande n'est pas encore prête pour la livraison."
              }
              description={`Statut actuel : ${order.status}`}
            />
          </Card>
        ) : order.order_type === "sur_place" ? (
          <Card>
            <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
              <span style={{
                width: 34, height: 34, borderRadius: R.md, flexShrink: 0,
                background: C.amberSoft, border: `1px solid ${C.amberBorder}`, color: C.amber,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon name="store" size={18} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: F.size.md, fontWeight: F.weight.semi, color: C.ink }}>
                  Vos vêtements sont prêts
                </div>
                <p style={{ fontSize: F.size.base, color: C.inkMuted, marginTop: 3, lineHeight: 1.55 }}>
                  Vous pouvez venir les récupérer sur place.
                </p>
                <Btn
                  icon="track"
                  style={{ marginTop: 12 }}
                  onClick={() => {
                    window.history.replaceState({}, "", `?track=${order.id}`);
                    window.location.reload();
                  }}
                >
                  Suivre ma commande
                </Btn>
              </div>
            </div>
          </Card>
        ) : (
          <>
            {/* ─── Livraison : quand nous RAPPORTONS le linge propre ───── */}
            <Card style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
                <span style={{
                  width: 30, height: 30, borderRadius: R.md, flexShrink: 0,
                  background: C.navy, color: C.inkOn,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Icon name="delivery" size={16} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.ink }}>
                    Date de livraison
                  </h3>
                  <p style={{ fontSize: F.size.base, color: C.inkMuted, marginTop: 2, lineHeight: 1.55 }}>
                    Votre linge est propre et prêt. Choisissez le jour où nous devons vous le
                    <strong> rapporter chez vous</strong>.
                  </p>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(92px, 1fr))", gap: 8 }}>
                {futureDates.map(d => {
                  const iso = d.toISOString().split("T")[0];
                  const dayName = DAYS_FR_LONG[d.getDay()];
                  const dayNum = d.getDate();
                  const month = d.toLocaleDateString("fr-FR", { month: "short" });
                  const isSelected = selectedDate === iso;
                  const isToday = d.toDateString() === new Date().toDateString();
                  return (
                    <button
                      key={iso}
                      type="button"
                      onClick={() => setSelectedDate(iso)}
                      aria-pressed={isSelected}
                      style={{
                        padding: "9px 6px", borderRadius: R.md, cursor: "pointer",
                        border: `1px solid ${isSelected ? C.navy : C.border}`,
                        background: isSelected ? C.navy : C.surface,
                        color: isSelected ? C.inkOn : C.inkBody,
                        textAlign: "center",
                        transition: "background .15s ease, border-color .15s ease, color .15s ease",
                      }}
                    >
                      <div style={{ fontSize: F.size.xs, fontWeight: F.weight.medium, opacity: .75 }}>{dayName}</div>
                      <div style={{ fontSize: F.size.xl, fontWeight: F.weight.semi, ...numeric }}>{dayNum}</div>
                      <div style={{ fontSize: F.size.xs, opacity: .75 }}>{month}</div>
                      {isToday && (
                        <div style={{
                          fontSize: F.size.xs, marginTop: 2, fontWeight: F.weight.medium,
                          color: isSelected ? C.inkOn : C.success,
                        }}>
                          Aujourd'hui
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Time slot selection */}
            {selectedDate && (
              <Card style={{ marginBottom: 12 }}>
                <h3 style={{
                  display: "flex", alignItems: "center", gap: 8, marginBottom: 3,
                  fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.ink,
                }}>
                  <Icon name="clock" size={17} color={C.inkMuted} />
                  Heure de livraison
                </h3>
                <p style={{ fontSize: F.size.base, color: C.inkMuted, marginBottom: 12 }}>
                  À quel moment de la journée souhaitez-vous être livré ?
                </p>
                {slotsLoading ? <Spinner /> : availableSlots.length === 0 ? (
                  <EmptyState
                    compact
                    icon="calendar"
                    title="Nous ne livrons pas ce jour-là"
                    description="Choisissez une autre date de livraison."
                  />
                ) : (
                  <div role="radiogroup" aria-label="Heure de livraison" style={{ display: "grid", gap: 8 }}>
                    {availableSlots.map(slot => {
                      const displayLabel = `${slot.start_time} — ${slot.end_time}`;
                      const isSelected = selectedSlot === slot.label;
                      return (
                        <button
                          key={slot.id}
                          type="button"
                          role="radio"
                          onClick={() => setSelectedSlot(slot.label)}
                          aria-checked={isSelected}
                          style={{
                            display: "flex", alignItems: "center", gap: 11,
                            minHeight: 48, padding: "11px 13px", borderRadius: R.md, cursor: "pointer",
                            border: `1px solid ${isSelected ? C.navy : C.border}`,
                            background: isSelected ? C.navySoft : C.surface,
                            textAlign: "left",
                            transition: "background .15s ease, border-color .15s ease",
                          }}
                        >
                          <span style={{
                            width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                            border: `1px solid ${isSelected ? C.navy : C.borderStrong}`,
                            background: isSelected ? C.navy : C.surface,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {isSelected && <Icon name="check" size={13} color={C.inkOn} strokeWidth={2.4} />}
                          </span>
                          <span style={{
                            fontWeight: F.weight.semi, fontSize: F.size.md,
                            color: isSelected ? C.navy : C.ink, ...numeric,
                          }}>
                            {displayLabel}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </Card>
            )}

            {/* Confirm button */}
            {selectedDate && selectedSlot && (
              <Card className="animate-fadeInUp">
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
                  fontSize: F.size.sm, fontWeight: F.weight.semi, color: C.inkMuted,
                  textTransform: "uppercase", letterSpacing: ".04em",
                }}>
                  <Icon name="receipt" size={13} />
                  Récapitulatif
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: F.size.base, marginBottom: 6 }}>
                  <span style={{ color: C.inkMuted }}>Date</span>
                  <span style={{ color: C.ink, fontWeight: F.weight.medium, textAlign: "right", ...numeric }}>
                    {new Date(selectedDate).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: F.size.base, marginBottom: 6 }}>
                  <span style={{ color: C.inkMuted }}>Créneau</span>
                  <span style={{ color: C.ink, fontWeight: F.weight.semi, textAlign: "right", ...numeric }}>{selectedSlot}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: F.size.base }}>
                  <span style={{ color: C.inkMuted }}>Adresse</span>
                  <span style={{ color: C.ink, textAlign: "right" }}>{order.address}</span>
                </div>

                <Btn
                  onClick={handleSubmit}
                  disabled={submitting}
                  loading={submitting}
                  fullWidth
                  icon="check"
                  style={{ marginTop: 16 }}
                >
                  {submitting ? "Confirmation…" : "Confirmer la livraison"}
                </Btn>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DeliveryChoice;
